import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';

const RecipeTranslationsDialog = ({ open, onClose, recipeId, recipeName, recipeNotes, api }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translations, setTranslations] = useState({
    en: { name: '', notes: '' },
    es: { name: '', notes: '' },
    fr: { name: '', notes: '' },
    it: { name: '', notes: '' },
    pt: { name: '', notes: '' }
  });

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'it', label: 'Italiano' },
    { code: 'pt', label: 'Português' }
  ];

  useEffect(() => {
    if (open && recipeId) {
      loadTranslations();
    }
  }, [open, recipeId]);

  const loadTranslations = async () => {
    setLoading(true);
    try {
      const data = await api.getRecipeTranslations(recipeId);
      const newTranslations = { ...translations };
      
      Object.keys(data).forEach(lang => {
        if (newTranslations[lang]) {
          newTranslations[lang] = {
            name: data[lang].name || '',
            notes: data[lang].notes || ''
          };
        }
      });
      
      setTranslations(newTranslations);
    } catch (error) {
      console.error('Error loading translations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (lang, field, value) => {
    setTranslations(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const lang of Object.keys(translations)) {
        const translation = translations[lang];
        
        // Solo guardar si hay contenido
        if (translation.name.trim()) {
          try {
            await api.updateRecipeTranslation(recipeId, lang, translation.name, translation.notes);
          } catch (error) {
            // Si no existe, intentar crear
            if (error.message.includes('404')) {
              await api.addRecipeTranslation(recipeId, lang, translation.name, translation.notes);
            } else {
              throw error;
            }
          }
        }
      }
      
      onClose(true); // true indica que se guardaron cambios
    } catch (error) {
      console.error('Error saving translations:', error);
      alert(t('error_saving_translations') || 'Error saving translations');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lang) => {
    if (!window.confirm(t('confirm_delete_translation') || 'Delete this translation?')) {
      return;
    }

    try {
      await api.deleteRecipeTranslation(recipeId, lang);
      setTranslations(prev => ({
        ...prev,
        [lang]: { name: '', notes: '' }
      }));
    } catch (error) {
      console.error('Error deleting translation:', error);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>{t('recipe_translations') || 'Recipe Translations'}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <strong>{t('original_recipe') || 'Original'}:</strong>
              <Box sx={{ mt: 1 }}>
                <strong>{t('name')}:</strong> {recipeName}
              </Box>
              {recipeNotes && (
                <Box sx={{ mt: 1 }}>
                  <strong>{t('notes')}:</strong> {recipeNotes}
                </Box>
              )}
            </Box>

            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
              {languages.map((lang, idx) => (
                <Tab key={lang.code} label={lang.label} />
              ))}
            </Tabs>

            {languages.map((lang, idx) => (
              <Box key={lang.code} hidden={activeTab !== idx}>
                <TextField
                  fullWidth
                  label={t('name')}
                  value={translations[lang.code].name}
                  onChange={(e) => handleChange(lang.code, 'name', e.target.value)}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('notes')}
                  value={translations[lang.code].notes}
                  onChange={(e) => handleChange(lang.code, 'notes', e.target.value)}
                  margin="normal"
                />
                {translations[lang.code].name && (
                  <Button
                    color="error"
                    size="small"
                    onClick={() => handleDelete(lang.code)}
                    sx={{ mt: 1 }}
                  >
                    {t('delete_translation') || 'Delete Translation'}
                  </Button>
                )}
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={saving}>
          {t('cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || loading}>
          {saving ? <CircularProgress size={24} /> : t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeTranslationsDialog;
